import * as React from 'react';
import {connect} from 'react-redux';

import * as ramda from 'ramda';
import ResizeObserver from 'resize-observer-polyfill';

import {getFilterableFields} from 'nutshell-core';

import {safelyGetRelationshipFilterIds} from 'nutshell-core/filter';
import {Routing} from 'nutshell-core/routing';
import {SavedFilters} from 'nutshell-core/saved-filters';
import * as Schema from 'nutshell-core/schema';
import type {
    CoreEntityType,
    FilterObject,
    LeadStatus,
    EntityListViewType,
} from 'nutshell-core/types';

import {LEAD_TOUR_CONTENT_AREA_ID} from 'shells/tours/tours';
import type {NutshellTour} from 'shells/tours/types';

import {getEntityGraduatedFilters} from '../../entities/entities-constants';
import {EntityList as EntityListStore} from '../../entity-list';
import {addNotification} from '../../notifications/index';
import type {NutshellState} from '../../store';
import {audienceFieldFilterFunction} from '../../ui/email-marketing/audiences/audience-list/audience-field-filter-function';

// This is the shared entity list directory
import {calculateInnerContentHeight} from '../core/entity-list/layout/calculate-inner-content-height';
import {calculateMainContentHeight} from '../core/entity-list/layout/calculate-main-content-height';
import {calculateMainContentWidth} from '../core/entity-list/layout/calculate-main-content-width';
import {FilterDrawerEntityListWrapper} from '../core/entity-list/layout/filter-drawer-entity-list-wrapper';
import {FilterDrawerMainContentWrapper} from '../core/entity-list/layout/filter-drawer-main-content-wrapper';
import {GetLeadCardFields} from '../lead/lead-card-dynamic/graphql/get-lead-card-fields';
import {AppMessagesProvider} from '../messages';
import {OnDemandReports} from '../reports/on-demand-reports';
import {VisualPipeline} from '../visual-pipeline/visual-pipeline';

import {EntityListFilterDrawer} from './entity-list-filter-drawer';
import {EntityTableWrapper} from './entity-table/entity-table-wrapper';
import {Footer} from './footer';
import {EntityListControls} from './header/entity-list-controls';
import {ListSummaryBar} from './header/list-summary/list-summary-bar';
import {EntityListTitleBarWrapper} from './header/title-bar/entity-list-title-bar-wrapper';
// @ts-expect-error
import {MapView} from './map-view';

import styles from './entity-list.module.css';

const leadsListSummaries = {
    totalValue: {
        label: 'Total value',
        key: 'value',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.sum,
        type: 'integer',
    },
    avgValue: {
        label: 'Avg value',
        key: 'value',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.mean,
        type: 'formattedInteger',
    },
    openedTime: {
        label: 'Avg time open',
        key: 'openedTime',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.mean,
        type: 'date',
    },
    winRate: {
        label: 'Win rate',
        key: 'winRate',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.value,
        type: 'percent',
    },
} as const;

type StatusFilter = {
    status:
        | LeadStatus
        | {
              data: [
                  {
                      data: LeadStatus;
                  },
              ];
          };
};

const validViews = {
    list: 'list',
    board: 'board',
    map: 'map',
    explore: 'explore',
} as const;
type ViewType = keyof typeof validViews;

// TODO (@ianvs): strengthen prop types
type OwnProps = {
    entityType: CoreEntityType;
    entity: any;
    notifications?: any[];
    tour?: NutshellTour;
};
type StateProps = {
    isFilterDrawerOpen: boolean;
};
type DispatchProps = {
    fetchSavedLists: () => void;
    setFilterDrawerOpen: (arg1: boolean) => void;
};

type Props = OwnProps & DispatchProps & StateProps;
type State = {
    scrollToColumnIndex: number;
};

function mapStateToProps(state: NutshellState): StateProps {
    return {
        isFilterDrawerOpen: EntityListStore.getIsFilterDrawerOpen(state),
    };
}

// @ts-expect-error
function mapDispatchToProps(dispatch): DispatchProps {
    return {
        fetchSavedLists: () => {
            dispatch(SavedFilters.fetchSavedLists());
        },
        setFilterDrawerOpen: (isOpen: boolean) => {
            dispatch(EntityListStore.setFilterDrawerOpen(isOpen));
        },
    };
}

export class EntityListComponent extends React.Component<Props, State> {
    pageContentNode: HTMLElement | null | undefined;
    headerNodeRef: HTMLElement | null | undefined;
    summaryBarNodeRef: HTMLElement | null | undefined;
    footerNodeRef: HTMLElement | null | undefined;
    headerWidth: number;
    headerHeight: number;
    headerTop: number;
    headerOffset: number;
    summaryBarHeight: number;
    footerHeight: number;
    pageContentHeight: number;
    mainContentHeight: number;
    // @ts-expect-error
    resizeObserver: ResizeObserver;
    lastNotificationId: string | null | undefined;

    constructor(props: Props) {
        super(props);
        props.entity.list.initialize();

        this.pageContentHeight = 650;
        this.footerHeight = 0;
        this.headerWidth = 1000;
        this.headerHeight = 118;
        this.headerTop = 50;
        this.headerOffset = 0;
        this.summaryBarHeight = 0;
        this.mainContentHeight = 650;
        this.lastNotificationId = null;

        this.state = {scrollToColumnIndex: 0};

        const type = props.entityType.charAt(0).toUpperCase() + props.entityType.slice(1);
        const analyticsAction = `initializeListBeta${type}`;
        Analytics.trackEvent(Analytics.categories.views, Analytics.actions.views[analyticsAction]);
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        // When going from a non-board view to the board view, we need to remove
        // the footer height and summary bar height, since they are not shown on that view
        const nextSelectedView = nextProps.entity.list.query.view.selected;
        const selectedView = this.props.entity.list.query.view.selected;
        if (nextSelectedView === 'board' && selectedView !== 'board') {
            this.footerHeight = 0;
            this.summaryBarHeight = 0;
        }
        if (nextSelectedView === 'explore' && selectedView !== 'explore') {
            this.footerHeight = 0;
        }
    }

    componentDidUpdate(prevProps: Props) {
        // If we went from board view to another view, we need to observe the
        // added summaryBar and footer
        const prevSelectedView = prevProps.entity.list.query.view.selected;
        const selectedView = this.props.entity.list.query.view.selected;
        const prevSelectedCount = prevProps.entity.list.selectedCount;
        const selectedCount = this.props.entity.list.selectedCount;

        // Added footer
        if (
            (prevSelectedView === 'board' || prevSelectedView === 'explore') &&
            (selectedView === 'list' || selectedView === 'map') &&
            this.footerNodeRef
        ) {
            this.resizeObserver.observe(this.footerNodeRef);
        }

        // Added summary bar
        if (prevSelectedView === 'board' && selectedView !== 'board' && this.summaryBarNodeRef) {
            this.resizeObserver.observe(this.summaryBarNodeRef);
        }

        // Handle footer appearing/disappearing due to selection
        if (prevSelectedCount !== selectedCount) {
            if (selectedCount > 0 && this.footerNodeRef) {
                this.resizeObserver.observe(this.footerNodeRef);
            } else if (selectedCount === 0) {
                if (this.footerNodeRef) {
                    this.resizeObserver.unobserve(this.footerNodeRef);
                }
                this.footerHeight = 0;
                this.forceUpdate();
            }
        }
    }

    componentDidMount() {
        // @ts-expect-error
        this.pageContentNode = document.querySelector('#app-react-mount');
        this.props.fetchSavedLists();

        // Check if we're in the saved-list-tour and ensure filter drawer is closed
        if (this.props.tour === 'saved-list-tour' && this.props.isFilterDrawerOpen) {
            this.props.setFilterDrawerOpen(false);
        }

        // Set up a resize observer to detect changes in layout-effecting dom elements,
        // and update the class static properties to reflect those changes, so that
        // the content can be sized correctly.
        this.resizeObserver = new ResizeObserver((entries) => {
            let shouldUpdate = false;
            for (const entry of entries) {
                switch (entry.target) {
                    case this.headerNodeRef: {
                        const header = entry.contentRect;
                        if (header.width !== this.headerWidth) {
                            this.headerWidth = header.width;
                            shouldUpdate = true;
                        }
                        if (header.height !== this.headerHeight) {
                            this.headerHeight = header.height;
                            shouldUpdate = true;
                        }
                        break;
                    }
                    case this.pageContentNode: {
                        const pageContent = entry.contentRect;
                        if (pageContent.height !== this.pageContentHeight) {
                            this.pageContentHeight = pageContent.height;
                            shouldUpdate = true;
                        }
                        break;
                    }
                    case this.footerNodeRef: {
                        const footer = entry.target;
                        if (footer.clientHeight !== this.footerHeight) {
                            // Using clientHeight to include margin
                            this.footerHeight = footer.clientHeight;
                            shouldUpdate = true;
                        }
                        break;
                    }
                    case this.summaryBarNodeRef: {
                        const summaryBar = entry.contentRect;
                        if (summaryBar.height !== this.summaryBarHeight) {
                            this.summaryBarHeight = summaryBar.height;
                            shouldUpdate = true;
                        }
                        break;
                    }
                    default:
                }
            }

            // If something that affects layout has changed, we need to re-render
            if (shouldUpdate) {
                this.forceUpdate();
            }
        });

        // Begin observing dom elements
        if (this.headerNodeRef) {
            this.resizeObserver.observe(this.headerNodeRef);
        }
        if (this.pageContentNode) {
            this.resizeObserver.observe(this.pageContentNode);
        }
        // Footer and summary bar are not on board view
        if (this.footerNodeRef) this.resizeObserver.observe(this.footerNodeRef);
        if (this.summaryBarNodeRef) this.resizeObserver.observe(this.summaryBarNodeRef);

        // Account for flash message or margin above header
        // Assumtion: this doesn't change after the initial page load
        const headerTop = this.headerNodeRef ? this.headerNodeRef.getBoundingClientRect().top : 0;
        const contentTop = this.pageContentNode
            ? this.pageContentNode.getBoundingClientRect().top
            : 0;
        this.headerOffset = headerTop - contentTop;

        $(window).on(Nut.events.global.entityWasCreated, this.refetchList);
        $(window).on(Nut.events.global.refetchEntityListPage, this.refetchPage);
    }

    componentWillUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        $(window).off(Nut.events.global.entityWasCreated, this.refetchList);
    }

    render() {
        const {
            entityType,
            notifications,
            entity: {
                displayName,
                displayNameSingular,
                list: entities,
                list: {
                    schema: {propertyMap: schemaPropertyMap},
                    query: {
                        view: {selected: selectedView, updateView},
                        audience: {selected: audienceId},
                    },
                },
            },
        } = this.props;

        const {
            query,
            query: {
                filter: {selected},
            },
        } = entities;

        // If the `view` param isn't valid, redirect to the default entity list url
        checkForValidView(selectedView);

        const statusFilter: StatusFilter | null | undefined = selected.find(
            // @ts-expect-error
            (filter) => Object.keys(filter)[0] === 'status'
        );

        const mostRecentNotification = notifications && ramda.head(notifications);
        if (mostRecentNotification && mostRecentNotification.id !== this.lastNotificationId) {
            this.lastNotificationId = mostRecentNotification.id;
            addNotification(mostRecentNotification.message);
        }

        // We recently moved to a different filter object schema, which is
        // a different shape than the previous one. Using our helper method
        // here because our status filter can be one of two types
        const appliedStatuses = statusFilter ? safelyGetRelationshipFilterIds(statusFilter) : [];

        // @ts-expect-error
        const stagesetFilter = selected.find((filter) => Object.keys(filter)[0] === 'stageset');
        const stagesetIdArray = stagesetFilter
            ? safelyGetRelationshipFilterIds(stagesetFilter)
            : null;
        const visualPipelineStagesetId = stagesetIdArray ? stagesetIdArray[0] : null;

        const showFooterWhenEntitySelected = entities.selectedCount > 0;

        const urlParams = Routing.deparam(window.location.search);
        const isViewingAudience = Boolean(urlParams.audience);

        const visibleFields = this.getVisibleFields(
            entityType,
            schemaPropertyMap,
            query.filter.selected,
            selectedView,
            isViewingAudience
        );
        const visibleFieldNames = visibleFields.map((field) => field.name);

        // @ts-expect-error
        const visibleActiveFilters = query.filter.selected.filter((filter) =>
            // @ts-expect-error
            visibleFieldNames.includes(Object.keys(filter)[0])
        );

        return (
            <div
                className={`ui-entity-list ui-entity-list--${entityType} ${styles['page-container']}`}
            >
                <div className={styles['content-wrapper']}>
                    <>
                        <div
                            ref={(node) => {
                                this.headerNodeRef = node;
                            }}
                        >
                            <EntityListTitleBarWrapper
                                entityType={entityType}
                                entities={entities}
                                shouldShowExportAndEmailButtons={
                                    selectedView !== 'board' && selectedView !== 'explore'
                                }
                                audienceId={audienceId}
                            />
                            <EntityListControls
                                currentView={selectedView}
                                entityType={entityType}
                                onViewChange={updateView}
                                schema={schemaPropertyMap}
                                activeFilters={query.filter.selected}
                                visibleActiveFilters={visibleActiveFilters}
                                isRefreshingFilters={entities.filtering}
                                entityCount={entities.count}
                                addFilters={query.filter.addFilters}
                                removeFilters={query.filter.removeFilters}
                                removeAllFilters={query.filter.removeAllFilters}
                                updateFilter={query.filter.updateFilter}
                                currentSearch={query.search.selected}
                                updateSearch={query.search.updateSearch}
                                isViewingAudience={isViewingAudience}
                            />
                        </div>
                        <FilterDrawerEntityListWrapper
                            mainContentHeight={calculateMainContentHeight({
                                pageContentHeight: this.pageContentHeight,
                                headerHeight: this.headerHeight,
                                headerOffset: this.headerOffset,
                                footerHeight: this.footerHeight,
                                hasNavbarOffset: true,
                                hasTrialStatusBarOffset: NutClientConfig.shouldShowTrialStatusBar,
                            })}
                        >
                            <EntityListFilterDrawer
                                // @ts-expect-error
                                currentView={selectedView}
                                entityType={entityType}
                                schema={schemaPropertyMap}
                                activeFilters={query.filter.selected}
                                isOpen={this.props.isFilterDrawerOpen}
                                addFilters={query.filter.addFilters}
                                removeFilters={query.filter.removeFilters}
                                updateFilter={query.filter.updateFilter}
                                shownColumns={entities.query.fields.selected[this.props.entityType]}
                                updateShownColumns={(fields, isAddingColumn) => {
                                    this.setState({
                                        scrollToColumnIndex: isAddingColumn ? -1 : 0,
                                    });
                                    entities.query.fields.setFields(fields);
                                }}
                                fields={visibleFields}
                            />
                            {this.renderMainContent(
                                selectedView,
                                entities,
                                appliedStatuses,
                                visualPipelineStagesetId
                            )}
                        </FilterDrawerEntityListWrapper>
                    </>
                    {selectedView !== 'board' &&
                    selectedView !== 'explore' &&
                    showFooterWhenEntitySelected ? (
                        <Footer
                            displayName={displayName}
                            displayNameSingular={displayNameSingular}
                            entityType={entityType}
                            entities={entities}
                            // @ts-expect-error
                            footerRef={(node) => {
                                this.footerNodeRef = node;
                            }}
                        />
                    ) : undefined}
                </div>
            </div>
        );
    }

    renderMainContent = (
        selectedView: ViewType,
        entities: any,
        appliedStatuses: Array<any>,
        visualPipelineStagesetId?: string | null
    ) => {
        const handleUpdateReportParams = entities.query.report
            ? entities.query.report.updateReportParams
            : () => {};

        const mainContentWidth = calculateMainContentWidth({
            width: this.headerWidth,
            isFilterDrawerOpen: this.props.isFilterDrawerOpen,
        });
        const innerContentHeight = calculateInnerContentHeight({
            pageContentHeight: this.pageContentHeight,
            headerHeight: this.headerHeight,
            headerOffset: this.headerOffset,
            summaryBarHeight: this.summaryBarHeight,
            footerHeight: this.footerHeight,
            hasNavbarOffset: true,
            hasTrialStatusBarOffset: NutClientConfig.shouldShowTrialStatusBar,
        });

        return (
            <FilterDrawerMainContentWrapper
                isFilterDrawerOpen={this.props.isFilterDrawerOpen}
                htmlId={this.props.entityType === 'leads' ? LEAD_TOUR_CONTENT_AREA_ID : undefined}
            >
                <div className={styles['app-messages-container']}>
                    <AppMessagesProvider type='Imports' />
                    {this.props.entityType === 'leads' ? (
                        // Only show the bulk created leads message if on the leads page
                        <AppMessagesProvider type='BulkCreateLeads' />
                    ) : undefined}
                    {this.props.entityType === 'contacts' && (
                        <AppMessagesProvider type='EmailSyncWithContacts' />
                    )}
                </div>
                {this.props.entityType === 'leads' &&
                NutClientConfig.featureFlagSummaryBar &&
                selectedView !== 'board' ? (
                    <div
                        className='ui-entity-list__table-header__bottom-row'
                        ref={(node) => {
                            this.summaryBarNodeRef = node;
                        }}
                    >
                        <ListSummaryBar
                            summaries={leadsListSummaries}
                            isFirstLoad={entities.firstLoad}
                            isFiltering={entities.filtering}
                            appliedStatuses={appliedStatuses}
                            stats={entities.leadStats}
                        />
                    </div>
                ) : undefined}
                {selectedView === 'explore' ? (
                    <OnDemandReports
                        filters={entities.query.filter.selected}
                        height={innerContentHeight}
                        width={mainContentWidth}
                        isScrollContainer={true}
                        onUpdateReportParams={handleUpdateReportParams}
                        reportGap={entities.query.report.selected.gap}
                        dateRangeType={entities.query.report.selected.type}
                        reportPivotFacet={entities.query.report.selected.pivotFacet}
                    />
                ) : undefined}
                {selectedView === 'map' ? (
                    <MapView
                        entities={entities}
                        mapFilter={getMapFilters(entities.query.filter.selected)}
                        changeView={entities.query.view.updateView}
                        height={innerContentHeight}
                        width={mainContentWidth}
                    />
                ) : undefined}
                {selectedView === 'list' ? (
                    <EntityTableWrapper
                        entities={entities}
                        entityType={this.props.entityType}
                        height={innerContentHeight}
                        width={mainContentWidth}
                        scrollToColumnIndex={this.state.scrollToColumnIndex}
                    />
                ) : undefined}
                {selectedView === 'board' ? (
                    <GetLeadCardFields stagesetId={visualPipelineStagesetId}>
                        {({fieldsToDisplay, isLoadingFields}) => (
                            <VisualPipeline
                                filters={entities.query.filter.selected}
                                sort={entities.query.sort}
                                search={entities.query.search.selected}
                                onToggleSort={entities.query.sort.toggleSort}
                                height={innerContentHeight}
                                width={mainContentWidth}
                                stagesetId={visualPipelineStagesetId}
                                fieldsToDisplay={fieldsToDisplay.map((field) => field.fieldId)}
                                isLoadingFields={isLoadingFields}
                            />
                        )}
                    </GetLeadCardFields>
                ) : undefined}
            </FilterDrawerMainContentWrapper>
        );
    };

    refetchList = () => {
        this.props.entity.list.watchForNew();
    };

    getVisibleFields = (
        entityType: CoreEntityType,
        schema: Schema.Properties,
        activeFilters: FilterObject[],
        currentView: EntityListViewType,
        isViewingAudience: boolean
    ): Schema.FieldProperties[] => {
        const graduatedFilters = getEntityGraduatedFilters(entityType);
        // @ts-expect-error
        const fieldFilterFunction = (fieldName) => {
            if (isViewingAudience && !audienceFieldFilterFunction(fieldName)) {
                return false;
            }

            return !graduatedFilters.includes(fieldName);
        };

        const filterableFields = getFilterableFields(schema, fieldFilterFunction);

        if (schema.map) {
            filterableFields.push(schema.map);
        }

        // Add funnelPeriod if it is active
        if (
            activeFilters.find((filter) => Object.keys(filter)[0] === 'funnelPeriod') &&
            schema.funnelPeriod
        ) {
            filterableFields.push(schema.funnelPeriod);
        }

        // If we're on the board view, filter out any milestone/stage related
        // filter from "filterable" fields.
        if (currentView === 'board') {
            // @ts-expect-error
            return filterableFields.filter((field) => {
                // Using stage and milestone here since we support both :/
                // Have to check for field, because schema might not have loaded yet
                return field && field.name !== 'stage' && field.name !== 'milestone';
            });
        }

        // @ts-expect-error
        return filterableFields;
    };

    refetchPage = () => {
        this.props.entity.list.refetchPage();
    };
}

export function getMapFilters(filters: FilterObject[]) {
    return ramda.find((entry) => {
        return Object.keys(entry)[0] === 'map' && typeof entry.map === 'object';
    }, filters);
}

/*
 * If the user has a malformed `view` param, instead of rendering a half-complete page, let's
 * redirect to the default params.
 */
function checkForValidView(currentView: string) {
    if (!Object.keys(validViews).includes(currentView)) {
        location.replace(
            `${location.protocol}//${location.hostname}${location.pathname}?restoreDefaults`
        );
    }
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);
const EntityList = connector(EntityListComponent);

export default EntityList;
